import React from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getLoggedInUserOptions, getUserOptions, editWorkflowTaskSubscriptionsOptions } from '@generated';
import Yup from '~/util/yup';
import { BuildingIcon } from 'lucide-react';
import { LibraryForm } from '../shared/LibraryForm';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckboxGroupFormField } from '~/shared/forms/controls/fields/CheckboxGroupFormField';
import { useIdentity } from '~/features/authentication/identity/IdentityContext';
import { useSearch } from '~/util/http/useSearch';

const validationSchema = Yup.object().shape({
	workflowStageTaskSubscriptionsIds: Yup.array().of(Yup.string()).label('Subscriptions')
});

export function EditUserSubscriptionsForm() {
	const { loggedInUser } = useIdentity();
	const { id } = useParams<'id'>();
	const navigate = useNavigate();
	const options = getUserOptions(id!);
	const { data: user } = useSuspenseQuery(options);
	const { data: workflowStageTaskDefinitions, isLoading } = useSearch({
		searchType: 'workflow-stage-task-definitions',
		filters: [{ name: 'subscribable', operator: '==', value: true }],
		pageSize: 1000,
		staleTime: Infinity,
		gcTime: Infinity
	});

	const workflowStageTaskDefinitionsOptions = isLoading
		? []
		: workflowStageTaskDefinitions.map(wstd => ({
				id: wstd.id,
				label: wstd.title
		  }));

	return (
		<LibraryForm
			initialValues={{
				userId: user.id,
				workflowStageTaskSubscriptionsIds: user.subscriptions?.map(s => s.workflowStageTaskDefinitionId) ?? []
			}}
			saveMutation={{
				...editWorkflowTaskSubscriptionsOptions,
				invalidateQueryKeys: [options.queryKey, ...(loggedInUser?.id === id ? [getLoggedInUserOptions().queryKey] : [])]
			}}
			title={`Edit Subscriptions: ${user.firstName} ${user.lastName}`}
			description={`Make changes to the user's subscriptions below.`}
			TitleIcon={BuildingIcon}
			validationSchema={validationSchema}
			errorAction={{
				errorActionText: 'Return to User Library',
				errorAction: () => navigate('/settings/libraries/users')
			}}
		>
			<div className="grid grid-cols-1">
				<CheckboxGroupFormField
					name="workflowStageTaskSubscriptionsIds"
					options={workflowStageTaskDefinitionsOptions}
					containerClassName="grid grid-cols-1 gap-4"
				/>
			</div>
		</LibraryForm>
	);
}
